export class TimeoutError extends Error {
  constructor(message = "Request timeout") {
    super(message);
    this.name = "TimeoutError";
  }
}

/**
 * Обёртка с таймаутом, устойчивая к фоновым вкладкам.
 *
 * Проблема: браузер приостанавливает таймеры и сетевые запросы в фоновой
 * вкладке (visibilityState === "hidden"). Если пользователь открыл приложение,
 * переключился на другую вкладку и вернулся, таймер мог «догореть» за время
 * паузы → ложный TimeoutError «Не удалось получить ответ от сервера», хотя
 * сервер ответил за миллисекунды.
 *
 * Решение: пока вкладка скрыта, таймер НЕ тикает (оставшееся время
 * замораживается). Как только вкладка снова видима — отсчёт продолжается.
 * Это не увеличивает реальный таймаут для активного пользователя, но
 * исключает ложные срабатывания при переключении вкладок.
 */
export const withTimeout = (promise, timeoutMs = 5000, message) => {
  const hasDocument =
    typeof document !== "undefined" && typeof document.visibilityState === "string";

  let timer = null;
  let remaining = timeoutMs;
  let start = 0;
  let settled = false;

  const startTimer = (reject) => {
    start = Date.now();
    timer = setTimeout(() => {
      settled = true;
      reject(new TimeoutError(message || `Timeout after ${timeoutMs}ms`));
    }, remaining);
  };

  const pauseTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
      remaining -= Date.now() - start;
      if (remaining < 0) remaining = 0;
    }
  };

  const onVisibility = (reject) => () => {
    if (document.visibilityState === "hidden") {
      pauseTimer();
    } else if (!settled && !timer) {
      startTimer(reject);
    }
  };

  return new Promise((resolve, reject) => {
    let cleanup = null;

    if (hasDocument) {
      const handler = onVisibility(reject);
      cleanup = () => document.removeEventListener("visibilitychange", handler);
      document.addEventListener("visibilitychange", handler);
      if (document.visibilityState !== "hidden") {
        startTimer(reject);
      }
    } else {
      // Нет document (SSR/тесты) — обычный таймаут
      timer = setTimeout(() => {
        settled = true;
        reject(new TimeoutError(message || `Timeout after ${timeoutMs}ms`));
      }, timeoutMs);
    }

    promise.then(
      (v) => {
        settled = true;
        if (timer) clearTimeout(timer);
        if (cleanup) cleanup();
        resolve(v);
      },
      (e) => {
        settled = true;
        if (timer) clearTimeout(timer);
        if (cleanup) cleanup();
        reject(e);
      },
    );
  });
};
